import axios from 'axios';
import {
    USER_DASHBOARD_LIST_REQUEST,
    USER_DASHBOARD_LIST_SUCCESS,
    USER_DASHBOARD_LIST_FAIL,
    USER_DASHBOARD_LIST_RESET,
    USER_DASHBOARD_BY_USER_REQUEST,
    USER_DASHBOARD_BY_USER_SUCCESS,
    USER_DASHBOARD_BY_USER_FAIL,
    USER_DASHBOARD_DEFAULT_REQUEST,
    USER_DASHBOARD_DEFAULT_SUCCESS,
    USER_DASHBOARD_DEFAULT_FAIL,
    USER_DASHBOARD_DETAILS_REQUEST,
    USER_DASHBOARD_DETAILS_SUCCESS,
    USER_DASHBOARD_DETAILS_FAIL,
    USER_DASHBOARD_DETAILS_RESET,
    USER_DASHBOARD_CREATE_REQUEST,
    USER_DASHBOARD_CREATE_SUCCESS,
    USER_DASHBOARD_CREATE_FAIL,
    USER_DASHBOARD_CREATE_RESET,
    USER_DASHBOARD_UPDATE_REQUEST,
    USER_DASHBOARD_UPDATE_SUCCESS,
    USER_DASHBOARD_UPDATE_FAIL,
    USER_DASHBOARD_UPDATE_RESET,
    USER_DASHBOARD_DELETE_REQUEST,
    USER_DASHBOARD_DELETE_SUCCESS,
    USER_DASHBOARD_DELETE_FAIL,
    USER_DASHBOARD_DTO_REQUEST,
    USER_DASHBOARD_DTO_SUCCESS,
    USER_DASHBOARD_DTO_FAIL,
    USER_DASHBOARD_DTO_RESET,
    USER_DASHBOARD_LIST_BY_IDS_REQUEST,
    USER_DASHBOARD_LIST_BY_IDS_SUCCESS,
    USER_DASHBOARD_LIST_BY_IDS_FAIL,
    USER_DASHBOARD_LIST_BY_IDS_RESET,
} from '../constants/userDashboardConstants';

const API_BASE_URL = '/dotnet_api/v1/UserDashboard';

const getAuthConfig = (token) => ({
    headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    },
});

const normalizeUserDashboardList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

export const listUserDashboards = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_LIST_REQUEST });

        const queryString = new URLSearchParams({
            page: params.page || 1,
            limit: params.limit || 100,
            sortBy: params.sortBy || 'displayOrder',
            sortOrder: params.sortOrder || 'asc',
            ...(params.filter && { filter: params.filter }),
            ...(params.query && { query: params.query }),
        }).toString();

        const { data } = await axios.get(
            `${API_BASE_URL}/all?${queryString}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_LIST_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_LIST_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const getUserDashboards = (userId) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_BY_USER_REQUEST });

        const { data } = await axios.get(
            `${API_BASE_URL}/user/${userId}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        const dashboards = normalizeUserDashboardList(data);

        dispatch({
            type: USER_DASHBOARD_BY_USER_SUCCESS,
            payload: dashboards,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_BY_USER_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

/** Lists dashboards for a user. Updates userDashboardByUser (source of truth for per-user rows).
 *  Does not use userDashboardList — that slice is shared with listUserDashboards (/all) for the
 *  reporting dropdown and would race with this fetch. */
export const listUserDashboardsForUser = (userId) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_BY_USER_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/user/${userId}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        const list = normalizeUserDashboardList(data);
        dispatch({ type: USER_DASHBOARD_BY_USER_SUCCESS, payload: list });
    } catch (error) {
        const message = error.response?.data?.message || error.message;
        dispatch({
            type: USER_DASHBOARD_BY_USER_FAIL,
            payload: message,
        });
    }
};

export const getDefaultDashboard = (userId) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_DEFAULT_REQUEST });

        const { data } = await axios.get(
            `${API_BASE_URL}/user/${userId}/default`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_DEFAULT_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_DEFAULT_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const getUserDashboardDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_DETAILS_REQUEST });

        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_DETAILS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_DETAILS_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const createUserDashboard = (dashboard) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_CREATE_REQUEST });

        const { data } = await axios.post(
            API_BASE_URL,
            dashboard,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_CREATE_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_CREATE_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const updateUserDashboard = (id, dashboard) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_UPDATE_REQUEST });

        const { data } = await axios.put(
            `${API_BASE_URL}/${id}`,
            dashboard,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_UPDATE_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_UPDATE_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const deleteUserDashboard = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_DELETE_REQUEST });

        await axios.delete(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );

        dispatch({
            type: USER_DASHBOARD_DELETE_SUCCESS,
            payload: id,
        });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_DELETE_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const loadUserDashboardDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_DTO_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: USER_DASHBOARD_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_DTO_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

export const listUserDashboardsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: USER_DASHBOARD_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids || [],
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: USER_DASHBOARD_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: USER_DASHBOARD_LIST_BY_IDS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};
